import { useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import useUserAccountValues from '@/accountSettings/utils/useUserAccountValues';
import {
  createOfferBuilderInitialState,
  OfferBuilderProvider,
  useOfferBuilderContext,
} from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { SetRateModalProvider } from '@/enterprise/components/OfferBuilder/OfferReview/SetRateModalContext';
import { ensureWorkingTakerMarkets } from '@/enterprise/components/OfferBuilder/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import { AcquisitionHydrationProvider } from './HydrateAcquisitionRates';
import GetPaidFasterModal from './modals/GetPaidFasterModal';
import HowC2foWorksModal from './modals/HowC2foWorksModal';
import useAcquisitionTakerMarkets from './useAcquisitionTakerMarkets';

type ActiveModal = 'howC2foWorks' | 'getPaidFaster' | null;

/**
 * acquisitionRatesHydrated is passed as a prop (local state from AcquisitionOfferSession)
 * to gate GetPaidFasterModal's open state, so it never shows with $0 estimates.
 * HowC2foWorksModal has no pricing dependency and opens immediately.
 */
const AcquisitionModals = ({
  activeModal,
  acquisitionRatesHydrated,
  onClose,
  takerMarkets,
}: {
  activeModal: ActiveModal;
  acquisitionRatesHydrated: boolean;
  onClose: () => void;
  takerMarkets: TakerMarket[];
}) => {
  const {
    state: { workingTakerMarkets },
  } = useOfferBuilderContext();

  if (activeModal === 'howC2foWorks') {
    return <HowC2foWorksModal open onClose={onClose} />;
  }

  if (activeModal === 'getPaidFaster') {
    return (
      <GetPaidFasterModal
        open={acquisitionRatesHydrated}
        onClose={onClose}
        takerMarkets={takerMarkets}
        workingTakerMarkets={workingTakerMarkets}
      />
    );
  }

  return null;
};

const AcquisitionOfferLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { email } = useUserAccountValues();

  const isOnConfirmationPage = location.pathname.includes('/confirmation');

  return (
    <SetRateModalProvider>
      <div className="flex flex-col items-start justify-between gap-6 pb-10 md:flex-row md:items-center">
        {isOnConfirmationPage ? (
          <div className="w-full text-center">
            <h1 className="m-0 text-4xl">{t('builder.thankYouForSubmittingOffer')}</h1>
            <div className="mt-4 font-medium">
              {t('offerConfirmation.title', {
                email,
              })}
            </div>
            <div className="mt-1 text-sm text-text-secondary">{t('offerConfirmation.subtitle')}</div>
          </div>
        ) : (
          <h1 className="m-0 text-4xl">{t('acquisition.oneClickEarlyPay')}</h1>
        )}
      </div>
      <Outlet />
    </SetRateModalProvider>
  );
};

const AcquisitionOfferSession = ({ takerMarkets }: { takerMarkets: TakerMarket[] }) => {
  const { trackEnterpriseEvent } = useReporting();
  const { addAction } = useDataDog();
  const [searchParams] = useSearchParams();
  const referrer = searchParams.get('referrer');
  const acquisitionCorrelationId = searchParams.get('correlationId') ?? undefined;
  const urlReferrer = searchParams.get('referrer') ?? undefined;
  const [activeModal, setActiveModal] = useState<ActiveModal>(
    referrer === 'microsites' ? 'howC2foWorks' : 'getPaidFaster'
  );

  // null = pricing not yet resolved; { hasRates } = pricing resolved
  const [hydratedState, setHydratedState] = useState<{ hasRates: boolean } | null>(null);

  useEffectOnce(() => {
    const url = new URL(window.location.href);
    const paramsToStrip = ['correlationId', 'referrer'];
    let changed = false;
    paramsToStrip.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
    if (changed) {
      window.history.replaceState({}, '', url.toString());
    }
  });

  useEffectOnce(() => {
    if (takerMarkets.length === 0) {
      return;
    }

    trackEnterpriseEvent('acquisition-offer::viewed', {
      takerMarketCount: takerMarkets.length,
      totalEligibleInvoiceAmountUsd: takerMarkets.reduce((sum, tm) => sum + tm.eligibleInvoiceAmountUsd, 0).toFixed(2),
      ...(acquisitionCorrelationId && { acquisitionCorrelationId }),
      ...(urlReferrer && { urlReferrer }),
    });
  });

  // Redirect after pricing loads if none of the markets returned a DISC trending rate —
  // there is nothing actionable to show on the review page.
  if (hydratedState !== null && !hydratedState.hasRates) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  // Rates start empty — HydrateAcquisitionRates (inside OfferReviewLayout) fills them in
  // once the pricing API responds, without blocking the OfferBuilderProvider mount.
  const workingTakerMarkets = ensureWorkingTakerMarkets({}, takerMarkets);

  const acquisitionState = createOfferBuilderInitialState({
    builderVariant: 'acquisition',
    frequency: 'oneTime',
    rateType: 'DISC',
    pricingStrategy: 'trendingRate',
    selected: takerMarkets,
    workingTakerMarkets,
    reviewOnly: true,
    disableNavigation: true,
    source: 'acquisition',
    acquisitionOfferProperties: {
      acquisitionCorrelationId,
      urlReferrer,
    },
  });

  return (
    <AcquisitionHydrationProvider
      onHydrated={(hasRates) => {
        if (!hasRates) {
          addAction('enterprise::acquisition-offer::no-disc-rates::redirected', {
            ...(acquisitionCorrelationId && { acquisitionCorrelationId }),
            ...(urlReferrer && { urlReferrer }),
          });
        }

        setHydratedState({ hasRates });
      }}
    >
      <OfferBuilderProvider initialState={acquisitionState} takerMarkets={takerMarkets}>
        <AcquisitionOfferLayout />
        <AcquisitionModals
          activeModal={activeModal}
          acquisitionRatesHydrated={hydratedState !== null}
          onClose={() => setActiveModal(null)}
          takerMarkets={takerMarkets}
        />
      </OfferBuilderProvider>
    </AcquisitionHydrationProvider>
  );
};

/**
 * Fetches acquisition-eligible taker markets and sets up the offer session.
 *
 * Two redirect guards protect the flow:
 *  1. Here — redirects immediately if no eligible taker markets were ever found.
 *  2. AcquisitionOfferSession — redirects after pricing loads if none of the markets
 *     returned a DISC trending rate.
 *
 * Pricing itself is loaded inside the OfferReviewLayout child route via
 * HydrateAcquisitionRates, which populates workingTakerMarkets in context without
 * blocking the OfferBuilderProvider mount.
 */
export const AcquisitionOfferComponent = () => {
  const { pathname } = useLocation();
  const allTakerMarkets = useAcquisitionTakerMarkets();
  const { addAction } = useDataDog();
  const [searchParams] = useSearchParams();

  // Snapshot on first valid result — post-submission the cache marks markets as 'isEnabled',
  // which empties this list. The frozen ref is used to keep the session alive on the
  // confirmation page where allTakerMarkets would otherwise be empty.
  const takerMarketsRef = useRef<typeof allTakerMarkets | null>(null);

  if (takerMarketsRef.current === null && allTakerMarkets.length > 0) {
    takerMarketsRef.current = allTakerMarkets;
  }

  const isOnConfirmationPage = pathname.includes('/confirmation');

  useEffect(() => {
    if (takerMarketsRef.current === null && allTakerMarkets.length === 0 && !isOnConfirmationPage) {
      const acquisitionCorrelationId = searchParams.get('correlationId') ?? undefined;
      const urlReferrer = searchParams.get('referrer') ?? undefined;
      addAction('enterprise::acquisition-offer::no-eligible-markets::redirected', {
        ...(acquisitionCorrelationId && { acquisitionCorrelationId }),
        ...(urlReferrer && { urlReferrer }),
      });
    }
  }, [addAction, allTakerMarkets.length, isOnConfirmationPage, searchParams]);

  // If no acquisition-eligible taker markets were ever loaded, redirect to build-offer.
  // The takerMarketsRef null check distinguishes this from a post-submit drain where
  // useAcquisitionTakerMarkets returns empty because submitted markets are no longer eligible.
  if (takerMarketsRef.current === null && allTakerMarkets.length === 0 && !isOnConfirmationPage) {
    return <Navigate to={asEnterpriseLink('build-offer')} replace />;
  }

  // On confirmation, submitted markets are no longer eligible; use the frozen list.
  const sessionTakerMarkets = isOnConfirmationPage ? takerMarketsRef.current ?? allTakerMarkets : allTakerMarkets;

  return <AcquisitionOfferSession takerMarkets={sessionTakerMarkets} />;
};

export default AcquisitionOfferComponent;
